import React, { useMemo, useState } from 'react';
import { Alert, Col, Row, Spinner, Tab, Tabs } from 'react-bootstrap';
import { getSalesId, getSalesValue, useSalesApiList } from '../hooks/useSalesApiResource';
import './PumpTableList.css';

const hpColors = {
  '15': '#ffffff',
  '20': '#ffff00',
  '25': '#92D050',
  '30': '#00B0F0',
  '40': '#FF0000',
  '50': '#00B050',
  '60': '#FF3399',
  '75': '#FFC000',
  '100': '#FCE4D6',
  '125': '#ED7D31',
};

const normalizePump = (pump = {}) => ({
  id: Number(getSalesId(pump) || 0),
  pumpModel: String(getSalesValue(pump, 'pumpModel', 'PumpModel') || ''),
  dischargePressure: String(getSalesValue(pump, 'dischargePressure', 'DischargePressure') || ''),
  pumpFlow: Number(getSalesValue(pump, 'pumpFlow', 'PumpFlow') || 0),
  pumpTypeId: Number(getSalesValue(pump, 'pampTypeId', 'PampTypeId', 'pumpTypeId', 'PumpTypeId') || 0),
  pumpHP: Number(getSalesValue(pump, 'pumpHP', 'PumpHP') || 0),
});

const normalizePumps = (rows) => rows.map(normalizePump);
const normalizeHp = (hp) => String(hp).replace(/\s+/g, '').replace(/HP$/i, '').toUpperCase();

const PumpTableList = () => {
  const [activeTab, setActiveTab] = useState('0');
  const [highlightedHP, setHighlightedHP] = useState(null);

  const { items: pumps, loading, error } = useSalesApiList((endpoints) => endpoints.pumpsReference.base, {
    normalize: normalizePumps,
    errorMessage: 'Unable to load pump table.',
  });

  const hpCategories = useMemo(() => Array.from(new Set(pumps.map((pump) => pump.pumpHP).filter(Boolean)))
    .sort((left, right) => left - right)
    .map((hp) => `${hp} HP`), [pumps]);

  const pumpGroups = useMemo(() => {
    const grouped = new Map();
    pumps.forEach((pump) => {
      const key = pump.pumpTypeId || 0;
      const current = grouped.get(key) || [];
      current.push(pump);
      grouped.set(key, current);
    });

    return Array.from(grouped.entries()).map(([pumpTypeId, rows]) => ({
      pumpTypeId,
      pumpName: `Pump Type ${pumpTypeId || 'Unassigned'}`,
      rows,
    }));
  }, [pumps]);

  const getHPColor = (hp) => hpColors[normalizeHp(hp)] || '#ccc';
  const isHpActive = (hp) => highlightedHP === null || normalizeHp(highlightedHP) === normalizeHp(hp);

  return (
    <section className="sales-portal-page" aria-labelledby="sales-pump-title">
      <Row className="mb-3">
        <Col><h4 id="sales-pump-title">Pump Table List</h4></Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="sales-no-data"><Spinner animation="border" size="sm" /> Loading pump table...</div>
      ) : (
        <>
          <div className="pump-table-legend mb-4">
            <div className="d-flex flex-wrap gap-2">
              {hpCategories.map((hp) => (
                <div
                  key={hp}
                  className="hp-category-box"
                  style={{ backgroundColor: getHPColor(hp), padding: '10px 20px', borderRadius: '4px', fontWeight: '600', color: '#000', cursor: 'pointer', transition: 'all 0.2s' }}
                  onMouseEnter={() => setHighlightedHP(hp)}
                  onMouseLeave={() => setHighlightedHP(null)}
                >
                  {hp.replace(' HP', 'Hp')}
                </div>
              ))}
            </div>
          </div>

          <Tabs activeKey={activeTab} onSelect={(eventKey) => eventKey && setActiveTab(eventKey)} className="mb-3 tabsec" mountOnEnter unmountOnExit>
            {pumpGroups.map((pumpGroup, index) => {
              const flowColumns = Array.from(new Set(pumpGroup.rows.map((pump) => pump.pumpFlow)))
                .filter(Boolean)
                .sort((left, right) => Number(left) - Number(right));
              const pressureRows = Array.from(new Set(pumpGroup.rows.map((pump) => pump.dischargePressure)))
                .filter(Boolean)
                .sort((left, right) => Number(left) - Number(right));

              return (
                <Tab key={pumpGroup.pumpTypeId} eventKey={String(index)} title={pumpGroup.pumpName}>
                  <div className="tableplumpouter">
                    <h5 className="mb-3">{pumpGroup.pumpName}</h5>

                    <div className="pump-table-container table-responsive">
                      <table className="table table-bordered pump-performance-table">
                        <thead>
                          <tr>
                            <th rowSpan={2} className="left-header-cell">
                              <div>Station</div>
                              <div>Discharge Pressure</div>
                            </th>
                            <th colSpan={flowColumns.length} className="flow-header-cell">Flow per pump (gpm)</th>
                          </tr>
                          <tr>
                            {flowColumns.map((flow) => (
                              <th key={`${pumpGroup.pumpTypeId}-flow-${flow}`} className="flow-cell-head">{flow}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {pressureRows.map((pressure) => (
                            <tr key={`${pumpGroup.pumpTypeId}-pressure-${pressure}`}>
                              <td className="discharge-cell">{pressure}</td>
                              {flowColumns.map((flow) => {
                                const option = pumpGroup.rows.find((pump) => pump.dischargePressure === pressure && pump.pumpFlow === flow);
                                return (
                                  <td
                                    key={`${pumpGroup.pumpTypeId}-${pressure}-${flow}`}
                                    className="pump-power-cell"
                                    style={{ backgroundColor: option && isHpActive(option.pumpHP) ? getHPColor(option.pumpHP) : 'transparent' }}
                                    title={option?.pumpModel}
                                  >
                                    <p className="power-value">{option?.pumpModel || '-'}</p>
                                    <span className="hp-value">{option?.pumpHP ? `${option.pumpHP} HP` : '-'}</span>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </Tab>
              );
            })}
          </Tabs>
        </>
      )}
    </section>
  );
};

export default PumpTableList;
